import { TagFilled, ZoomInOutlined } from "@ant-design/icons";
import { Button, Tag } from "antd";
import cn from "classnames";
import { last } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { useRouteMatch } from "react-router";

import { CLOUD_API_URL } from "app/env";
import { galleryContext } from "components/common/gallery/context";
import SuspendingImage from "components/common/SuspendingImage";
import { useDispatch, useSelector } from "hooks/redux";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useShowInternalSettings } from "hooks/useIsAdmin";
import { useOrgDataLevel } from "hooks/useOrgDataLevel";
import { hasSelectedFiles, isFileSelected } from "state/file_selector/selectors";
import { fileSelectorActions } from "state/file_selector/slice";
import { formatVideoTime } from "utils/common";
import { iconPath } from "utils/paths";
import { trackUserAction } from "utils/trackEvent";

import { FallbackFileCard } from "./FallbackFileCard";
import { RtkFileCard } from "./RtkFileCard";
import { getReleaseStatusTagText } from "./utils";

import styles from "./FileCard.module.scss";

import type { FileCard_file$data, FileCard_file$key } from "__generated__/FileCard_file.graphql";

const fileFragment = graphql`
  fragment FileCard_file on FlightDataFile {
    id
    uuid
    filename
    kind
    sizeBytes
    thumbnailUrl
    isUploaded
    flightId
    fileUsage
    isCji
    metadata {
      core {
        durationUs
      }
    }
    annotationCount: annotations {
      totalCount
    }
  }
`;

export interface Size {
  height: string;
  width: string;
}

const VIDEO_TYPES = new Set(["vehicleVideoPreview", "vehicleVideoRaw"]);
export const RTK_ATTACHMENT_TYPES = new Set([
  "rtkAttachmentRinexObs",
  "rtkAttachmentRinexNav",
  "rtkAttachmentSbf",
  "rtkAttachmentMrk",
  "rtkAttachmentRinexObsScan",
  "rtkAttachmentRinexNavScan",
  "rtkAttachmentSbfScan",
  "rtkAttachmentMrkScan",
]);

type VideoOverlayProps = {
  file: FileCard_file$data;
  handleClick: () => void;
};

const VideoOverlay: React.FC<VideoOverlayProps> = ({ file, handleClick }) => {
  const fileExtension = last(file.filename.split("."))?.toUpperCase();
  return (
    <div className={styles.overlay} onClick={handleClick}>
      <img className={styles.playIcon} src={iconPath("play-circle-white-fill")} alt="" />
      <div className={styles.videoMetaContainer}>
        {fileExtension && <div className={styles.tagContainer}>{fileExtension}</div>}
        {!!file.metadata?.core?.durationUs && (
          <div className={styles.tagContainer}>
            {formatVideoTime(file.metadata.core.durationUs)}
          </div>
        )}
      </div>
    </div>
  );
};

const selectionColorStyle = {
  color: "#FFF",
};

const tagFilledIcon = <TagFilled />;
const zoomIcon = <ZoomInOutlined className={styles.zoomIcon} />;

interface Props {
  fileRef: FileCard_file$key;
  isLastEdge: boolean;
  size?: Size;
  highlighted?: boolean;
  readonly?: boolean;
}

const FileCard: React.FC<Props> = ({
  fileRef,
  size,
  highlighted,
  isLastEdge,
  readonly = false,
}) => {
  const file = useFragment(fileFragment, fileRef);
  const { openGallery } = useContext(galleryContext);
  const annotationCount = file.annotationCount?.totalCount || 0;
  const sharedLinkMatch = useRouteMatch<{ token: string }>();
  const orgDataLevel = useOrgDataLevel();

  const dispatch = useDispatch();

  const inSelectionMode = useSelector(hasSelectedFiles);
  const selected = useSelector(state => isFileSelected(state, file.uuid, file.flightId));
  const enableViewDownloadRtkPpkFiles = useFeatureFlag("enableViewDownloadRtkPpkFiles");
  const showReleaseUi = useFeatureFlag("showFileReleaseUi");
  const showInternal = useShowInternalSettings();

  const showStatusTag = showReleaseUi && showInternal;

  useEffect(() => {
    trackUserAction("file_seen", { uuid: file.uuid });
  }, [file.uuid]);

  const handleChange = useCallback(
    (e: { target: { checked: boolean } }) => {
      if (e.target.checked) {
        trackUserAction("selected_file", { fileUuid: file.uuid });
        dispatch(fileSelectorActions.selectFile(file));
      } else {
        trackUserAction("unselected_file", { fileUuid: file.uuid });
        dispatch(fileSelectorActions.deselectFile(file));
      }
    },
    [dispatch, file]
  );

  const openGalleryToFlight = useCallback(() => {
    trackUserAction("media.open_gallery", { flightId: file.flightId, fileId: file.id });
    openGallery(file.flightId, file.id);
  }, [file.flightId, file.id, openGallery]);

  const handleClick = useCallback(() => {
    if (inSelectionMode) {
      handleChange({ target: { checked: !selected } });
    } else {
      openGalleryToFlight();
    }
  }, [inSelectionMode, openGalleryToFlight, selected, handleChange]);

  const sizeStyle = useMemo(() => ({ ...size }), [size]);

  const releaseStatusTag = useMemo(() => {
    const text = getReleaseStatusTagText(file, orgDataLevel);
    if (text.length === 0) {
      return null;
    }
    return (
      // indianred tag
      <Tag color="#CD5D5C" className={styles.releaseStatusTag}>
        {text}
      </Tag>
    );
  }, [file, orgDataLevel]);

  if (isLastEdge) {
    return <FallbackFileCard size={size} />;
  }

  // File hasn't uploaded at all yet
  if (!file.isUploaded) {
    return <FallbackFileCard file={file} size={size} />;
  }

  if (enableViewDownloadRtkPpkFiles && RTK_ATTACHMENT_TYPES.has(file.kind)) {
    return (
      <RtkFileCard
        file={file}
        size={size}
        onClick={handleClick}
        readonly={readonly}
        selected={selected}
        selecting={inSelectionMode}
        handleCheckboxChange={handleChange}
      />
    );
  }

  // File was uploaded but thumbnail doesn't exist
  if (!file.thumbnailUrl) {
    return (
      <FallbackFileCard
        file={file}
        size={size}
        onClick={handleClick}
        readonly={readonly}
        selected={selected}
        selecting={inSelectionMode}
        handleCheckboxChange={handleChange}
      />
    );
  }

  const src = file.thumbnailUrl;
  let absoluteUrl = src?.startsWith("/") ? CLOUD_API_URL + src : src ?? "";
  if (sharedLinkMatch?.params.token) {
    absoluteUrl += `?shared_link_token=${sharedLinkMatch.params.token}`;
  }

  return (
    <div
      className={cn(
        styles.cardContainer,
        styles.stacking,
        highlighted ? styles.highlighted : undefined
      )}
      style={sizeStyle}
    >
      <SuspendingImage
        alt={file.filename}
        className={styles.background}
        src={absoluteUrl}
        onClick={handleClick}
      />
      {VIDEO_TYPES.has(file.kind) && <VideoOverlay file={file} handleClick={handleClick} />}
      {!readonly && (
        <input
          className={inSelectionMode ? styles.selectedCheckbox : styles.hoverableCheckbox}
          type="checkbox"
          checked={selected}
          onChange={handleChange}
        />
      )}
      {inSelectionMode && (
        <Button
          type="text"
          style={selectionColorStyle}
          icon={zoomIcon}
          className={styles.hoverableZoombutton}
          onClick={openGalleryToFlight}
        />
      )}
      {!!annotationCount && (
        <Tag className={styles.annotationTag} icon={tagFilledIcon} color="#AD4E00">
          {annotationCount}
        </Tag>
      )}
      {showStatusTag && releaseStatusTag}
    </div>
  );
};

export default FileCard;
